import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Zap, Lock, Loader2, AlertCircle, Mail } from "lucide-react";
import api, { formatApiError, setActiveWorkspaceId } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * /register — two modes:
 *   (a) No ?invite= param        → invite-only message, point to /login.
 *   (b) ?invite=<token>          → fetch invite preview, then either:
 *         - new user form (name + password)  → POST /auth/register-with-invite
 *         - existing user: must sign in then auto-accept via /auth/accept-invite
 */
export default function Register() {
  const [params] = useSearchParams();
  const token = params.get("invite");
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [previewError, setPreviewError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", password: "", login_email: "", login_password: "" });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/invites/${token}/preview`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setPreviewError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // If a logged-in user opens an invite link, auto-attempt to accept.
  useEffect(() => {
    if (!token || !user || !preview) return;
    if (user.email?.toLowerCase() !== preview.email?.toLowerCase()) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/accept-invite", { token });
        setActiveWorkspaceId(data.workspace_id);
        toast.success(`Joined "${preview.workspace_name}"`);
        window.location.assign("/");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [token, user, preview]);

  const submitNewUser = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-with-invite", {
        token,
        name: form.name.trim(),
        password: form.password,
      });
      localStorage.setItem("lf_token", data.token);
      setActiveWorkspaceId(data.workspace_id);
      toast.success(`Welcome to ${preview.workspace_name}`);
      window.location.assign("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitExistingUserLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(form.login_email || preview.email, form.login_password);
      if (!result.ok) {
        toast.error(result.error || "Login failed");
        return;
      }
      // useEffect above will accept invite once user is set.
    } finally {
      setSubmitting(false);
    }
  };

  // ----- No invite param: invite-only message --------------------------------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6 py-12" data-testid="register-closed">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black text-lg tracking-tight">LEADFORGE AI</div>
          </div>
          <div className="overline mb-3">/ invite-only access</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-none mb-4">
            Registration is<br />by invitation.
          </h1>
          <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
            LeadForge AI is a closed platform. New accounts can only be created
            through an invite link generated by a workspace admin.
          </p>
          <div className="border-l-4 border-[#002FA7] bg-zinc-50 px-4 py-3 mb-8 flex items-start gap-3">
            <Lock className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" />
            <div className="text-xs text-zinc-700">
              Already have an account?{" "}
              <Link to="/login" className="font-bold text-[#002FA7] hover:underline" data-testid="link-login">
                Sign in here
              </Link>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ----- Invite preview loading ----------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="register-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  // ----- Invite invalid / expired --------------------------------------------
  if (previewError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="register-invite-error">
        <div className="max-w-md text-center">
          <div className="w-12 h-12 bg-red-100 mx-auto flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="font-heading font-black text-2xl mb-2">Invite unavailable</h1>
          <p className="text-sm text-zinc-600 mb-6">{previewError}</p>
          <Link to="/login" className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline">
            Go to sign in →
          </Link>
        </div>
      </div>
    );
  }

  // ----- Logged-in user viewing invite ---------------------------------------
  if (user && preview) {
    const sameEmail = user.email?.toLowerCase() === preview.email?.toLowerCase();
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="register-logged-in-context">
        <div className="max-w-md text-center">
          <div className="overline mb-3">/ workspace invite</div>
          <h1 className="font-heading font-black text-3xl mb-4 leading-tight">
            Join "{preview.workspace_name}" as <span className="text-[#002FA7]">{preview.role}</span>?
          </h1>
          {sameEmail ? (
            <p className="text-sm text-zinc-600 mb-6">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
              Accepting invite for {user.email}…
            </p>
          ) : (
            <>
              <p className="text-sm text-zinc-600 mb-6">
                This invite is for <span className="font-mono font-bold">{preview.email}</span>, but you're signed in as <span className="font-mono font-bold">{user.email}</span>. Sign out and use the correct account.
              </p>
              <Link to="/login" className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline">
                Switch account →
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  // ----- New user vs. existing user signup -----------------------------------
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-12" data-testid="register-invite-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-black text-lg tracking-tight">LEADFORGE AI</div>
        </div>

        <div className="overline mb-3">/ you've been invited</div>
        <h1 className="font-heading font-black text-3xl tracking-tight leading-tight mb-2">
          Join "{preview.workspace_name}"
        </h1>
        <p className="text-sm text-zinc-600 mb-6 flex items-center gap-2">
          <Mail className="w-4 h-4 text-zinc-400" />
          <span className="font-mono font-bold">{preview.email}</span>
          <span className="text-zinc-400">·</span>
          <span className="uppercase tracking-wider font-semibold">{preview.role}</span>
        </p>

        {preview.user_already_exists ? (
          <form onSubmit={submitExistingUserLogin} className="space-y-3" data-testid="register-existing-user-form">
            <div className="border-l-4 border-[#002FA7] bg-zinc-50 px-4 py-3 text-xs text-zinc-700 mb-4">
              An account for <span className="font-mono font-bold">{preview.email}</span> already exists. Sign in to accept this invite.
            </div>
            <input
              data-testid="login-password-input"
              type="password"
              required
              placeholder="Password"
              value={form.login_password}
              onChange={(e) => setForm((f) => ({ ...f, login_password: e.target.value }))}
              className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#002FA7] text-sm"
            />
            <button
              data-testid="login-and-accept-btn"
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-[#002FA7] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#001F73] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in & accept invite
            </button>
          </form>
        ) : (
          <form onSubmit={submitNewUser} className="space-y-3" data-testid="register-new-user-form">
            <input
              data-testid="signup-name-input"
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#002FA7] text-sm"
            />
            <input
              data-testid="signup-password-input"
              type="password"
              required
              minLength={8}
              placeholder="Password (8+ chars)"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#002FA7] text-sm"
            />
            <button
              data-testid="signup-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-[#002FA7] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#001F73] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Create account & join
            </button>
          </form>
        )}

        <div className="mt-6 text-xs text-zinc-500">
          By joining you get access to <span className="font-semibold">{preview.workspace_name}</span>'s leads, campaigns and AI agents.
        </div>
      </div>
    </div>
  );
}
